import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { monitoringService, JobSearchResult, JobSearchFilters, JobsFilterOptions } from '@/services/monitoring'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { JobStatusBadge } from '../shared/JobStatusBadge'
import { JobProgressBar } from '../shared/JobProgressBar'
import { RefreshCw, Search, X, RotateCcw, Filter } from 'lucide-react'
import { toast } from 'sonner'

// Export the filter type for use in monitoring page
export interface JobsFilter {
  batchTimestamp?: string
  filterType?: 'hourly' | 'daily' | 'reprocess' | 'backfill' | 'email' | 'push' | 'total' | 'completed' | 'pending' | 'running' | 'errors'
  batchLabel?: string
  integrationId?: number
  integrationName?: string
}

interface JobsExplorerProps {
  initialFilter?: JobsFilter | null
  onFilterClear?: () => void
  organizationId?: number
}

/**
 * Jobs Explorer - Tab for searching and viewing jobs
 *
 * Features:
 * - Smart filters from Live Monitor (click cells to filter)
 * - Multi-select status filters (checkboxes)
 * - Search by integration, organization, or job ID
 * - Progress bars with ETA
 * - Status badges with colors
 * - Pagination
 */
// Helper to compute initial kind filters based on filter type
function getInitialKindFilters(filter: JobsFilter | null): Set<string> {
  if (!filter) return new Set(['hourly', 'daily', 'reprocess', 'backfill', 'email'])
  switch (filter.filterType) {
    case 'hourly':
    case 'daily':
    case 'reprocess':
    case 'backfill':
    case 'email':
      return new Set([filter.filterType])
    default:
      return new Set(['hourly', 'daily', 'reprocess', 'backfill', 'email'])
  }
}

// Helper to compute initial status filters based on filter type
function getInitialStatusFilters(filter: JobsFilter | null): Set<string> {
  if (!filter) return new Set(['pending', 'running', 'completed', 'failed'])
  switch (filter.filterType) {
    case 'completed':
      return new Set(['completed'])
    case 'running':
      return new Set(['running'])
    case 'pending':
      // Pending = only queued jobs (matching Live Collection's definition)
      return new Set(['pending'])
    case 'errors':
      // Show all statuses when using has_errors filter
      return new Set(['pending', 'running', 'completed', 'failed'])
    case 'hourly':
    case 'daily':
    case 'reprocess':
    case 'backfill':
    case 'total':
    default:
      return new Set(['pending', 'running', 'completed', 'failed'])
  }
}

export function JobsExplorer({ initialFilter, onFilterClear, organizationId }: JobsExplorerProps) {
  const { orgSlug } = useParams<{ orgSlug: string }>()
  const [jobs, setJobs] = useState<JobSearchResult[]>([])
  const [totalJobs, setTotalJobs] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date())

  // Smart filter from performance table (only for batch timestamp now)
  const [smartFilter, setSmartFilter] = useState<JobsFilter | null>(initialFilter || null)

  // Status filter checkboxes - initialized based on initialFilter
  const [statusFilters, setStatusFilters] = useState<Set<string>>(() => getInitialStatusFilters(initialFilter || null))

  // Kind filter checkboxes - initialized based on initialFilter
  const [kindFilters, setKindFilters] = useState<Set<string>>(() => getInitialKindFilters(initialFilter || null))

  // Selection for bulk retry
  const [selectedJobIds, setSelectedJobIds] = useState<Set<number>>(new Set())
  const [retryingJobs, setRetryingJobs] = useState<Set<number>>(new Set())

  // Search term (sent to backend)
  const [searchTerm, setSearchTerm] = useState('')

  // Organization filter (set of org IDs)
  const [orgFilters, setOrgFilters] = useState<Set<number>>(new Set())

  // Filter options from backend (available organizations)
  const [filterOptions, setFilterOptions] = useState<JobsFilterOptions>({ organizations: [] })

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(50)

  // Period selector (hours)
  const [hours, setHours] = useState<number>(24)

  // Has errors filter (for jobs with failed/exhausted periods, not just failed status)
  const [hasErrorsFilter, setHasErrorsFilter] = useState<boolean>(initialFilter?.filterType === 'errors')

  // Pending filter (only queued jobs, matching Live Collection's definition)
  const [isPendingFilter, setIsPendingFilter] = useState<boolean>(initialFilter?.filterType === 'pending')

  // Actually running filter (jobs with status=running AND at least one period running, matching Live Collection)
  const [isActuallyRunningFilter, setIsActuallyRunningFilter] = useState<boolean>(initialFilter?.filterType === 'running')

  // Update filters when initialFilter prop changes (from Live Collection clicks)
  useEffect(() => {
    if (initialFilter) {
      setSmartFilter(initialFilter)
      setCurrentPage(1)

      // Reset special filters first
      setHasErrorsFilter(false)
      setIsPendingFilter(false)
      setIsActuallyRunningFilter(false)

      // Handle integration filter (from Behavior tab anomalies click)
      if (initialFilter.integrationId || initialFilter.integrationName) {
        // Set search term to integration name or ID
        setSearchTerm(initialFilter.integrationName || String(initialFilter.integrationId))
        // Show all statuses and kinds for integration view
        setStatusFilters(new Set(['pending', 'running', 'completed', 'failed']))
        setKindFilters(new Set(['hourly', 'daily', 'reprocess', 'backfill', 'email']))
        setHasErrorsFilter(true) // Show jobs with errors for this integration
        return
      }

      // Map filterType to column filters
      switch (initialFilter.filterType) {
        case 'hourly':
        case 'daily':
        case 'reprocess':
        case 'backfill':
        case 'email':
          // Set kind filter to just this type
          setKindFilters(new Set([initialFilter.filterType]))
          setStatusFilters(new Set(['pending', 'running', 'completed', 'failed']))
          break
        case 'completed':
          setStatusFilters(new Set(['completed']))
          setKindFilters(new Set(['hourly', 'daily', 'reprocess', 'backfill', 'email']))
          break
        case 'running':
          // Running = jobs with status=running (matches Live Collection's "in_progress" count)
          setStatusFilters(new Set(['running']))
          setKindFilters(new Set(['hourly', 'daily', 'reprocess', 'backfill', 'email']))
          break
        case 'pending':
          // Pending = only queued jobs (status=0)
          // This matches Live Collection's "pending" definition exactly
          setStatusFilters(new Set(['pending']))
          setKindFilters(new Set(['hourly', 'daily', 'reprocess', 'backfill', 'email']))
          break
        case 'errors':
          // Use has_errors filter to find jobs with failed/exhausted periods (not just failed status)
          setHasErrorsFilter(true)
          setStatusFilters(new Set(['pending', 'running', 'completed', 'failed']))
          setKindFilters(new Set(['hourly', 'daily', 'reprocess', 'backfill', 'email']))
          break
        case 'total':
        default:
          // Show all
          setStatusFilters(new Set(['pending', 'running', 'completed', 'failed']))
          setKindFilters(new Set(['hourly', 'daily', 'reprocess', 'backfill', 'email']))
          break
      }
    }
  }, [initialFilter])

  // Fetch on mount and when filters change
  useEffect(() => {
    fetchJobs()
  }, [smartFilter, statusFilters, kindFilters, currentPage, hours, hasErrorsFilter, isPendingFilter, isActuallyRunningFilter, searchTerm, orgFilters, organizationId])

  const fetchJobs = async () => {
    try {
      setLoading(true)
      setError(null)

      // Build filters for the search API
      const filters: JobSearchFilters = {
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage
      }

      // Set date range - either from smart filter (specific batch) or hours period
      if (smartFilter?.batchTimestamp) {
        const batchStart = new Date(smartFilter.batchTimestamp)
        const batchEnd = new Date(batchStart.getTime() + 60 * 60 * 1000) // +1 hour
        filters.start_date = batchStart.toISOString()
        filters.end_date = batchEnd.toISOString()
      } else {
        const now = new Date()
        const startDate = new Date(now.getTime() - hours * 60 * 60 * 1000)
        filters.start_date = startDate.toISOString()
        filters.end_date = now.toISOString()
      }

      // Apply status filters
      const selectedStatuses = Array.from(statusFilters)
      if (selectedStatuses.length > 0 && selectedStatuses.length < 4) {
        filters.status = selectedStatuses.join(',')
      }

      // Apply kind filter - API only supports single kind, so only filter when one is selected
      const selectedKinds = Array.from(kindFilters).map(k => k.toLowerCase())
      if (selectedKinds.length === 1) {
        filters.kind = selectedKinds[0]
      }
      // When multiple kinds selected (but not all 4), we'll filter on frontend

      // Apply has_errors filter (for jobs with failed/exhausted periods)
      if (hasErrorsFilter) {
        filters.has_errors = true
      }

      // Apply actually running filter (for jobs with status=running AND at least one period running)
      // This matches Live Collection's "in_progress" definition
      if (isActuallyRunningFilter) {
        filters.is_actually_running = true
      }

      // Apply pending dispatch filter (for running jobs that haven't started processing yet)
      if (isPendingFilter) {
        filters.is_pending_dispatch = true
      }

      // Apply search filter (searches in integration ID and name on backend)
      if (searchTerm.trim()) {
        filters.search = searchTerm.trim()
      }

      // Apply organization filter - use parent's organizationId if provided, otherwise use orgFilters
      if (organizationId) {
        filters.org_id = organizationId
      } else if (orgFilters.size > 0) {
        filters.org_ids = Array.from(orgFilters).join(',')
      }

      console.log('[JobsExplorer] Fetching with filters:', JSON.stringify(filters, null, 2), 'smartFilter:', smartFilter, 'kindFilters:', Array.from(kindFilters))
      const response = await monitoringService.searchJobs(filters)
      console.log('[JobsExplorer] Response:', response.total, 'jobs found')
      setJobs(response.jobs)
      setTotalJobs(response.total)
      setLastRefresh(new Date())
      // Store filter options (available organizations)
      if (response.filter_options) {
        setFilterOptions(response.filter_options)
      }
    } catch (err: any) {
      console.error('Failed to fetch jobs:', err)
      setError(err.response?.data?.detail || 'Failed to load jobs')
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = () => {
    fetchJobs()
  }

  // Retry a single failed job
  const handleRetryJob = async (jobId: number) => {
    try {
      setRetryingJobs(prev => new Set([...prev, jobId]))
      const result = await monitoringService.retryFailedJob(jobId)
      toast.success(`Retried ${result.periods_reset} periods for job ${jobId}`)
      // Clear selection and refresh
      setSelectedJobIds(prev => {
        const next = new Set(prev)
        next.delete(jobId)
        return next
      })
      fetchJobs()
    } catch (err: any) {
      toast.error(err.response?.data?.detail || `Failed to retry job ${jobId}`)
    } finally {
      setRetryingJobs(prev => {
        const next = new Set(prev)
        next.delete(jobId)
        return next
      })
    }
  }

  // Bulk retry selected jobs
  const handleBulkRetry = async () => {
    if (selectedJobIds.size === 0) return

    // Confirm if retrying more than 10 jobs
    if (selectedJobIds.size > 10) {
      const confirmed = window.confirm(
        `You are about to retry ${selectedJobIds.size} jobs. This may queue many tasks. Continue?`
      )
      if (!confirmed) return
    }

    const jobIds = Array.from(selectedJobIds)
    let totalRetried = 0
    let errorCount = 0

    // Retry jobs sequentially to avoid overwhelming the server
    for (const jobId of jobIds) {
      try {
        setRetryingJobs(prev => new Set([...prev, jobId]))
        const result = await monitoringService.retryFailedJob(jobId)
        totalRetried += result.periods_reset
      } catch (err) {
        errorCount++
      } finally {
        setRetryingJobs(prev => {
          const next = new Set(prev)
          next.delete(jobId)
          return next
        })
      }
    }

    if (errorCount > 0) {
      toast.warning(`Retried ${totalRetried} periods (${errorCount} jobs had errors)`)
    } else {
      toast.success(`Retried ${totalRetried} periods across ${jobIds.length} jobs`)
    }

    setSelectedJobIds(new Set())
    fetchJobs()
  }

  // Toggle job selection
  const handleToggleSelection = (jobId: number) => {
    setSelectedJobIds(prev => {
      const next = new Set(prev)
      if (next.has(jobId)) {
        next.delete(jobId)
      } else {
        next.add(jobId)
      }
      return next
    })
  }

  // Select all failed jobs
  const handleSelectAllFailed = () => {
    const failedJobIds = displayJobs
      .filter(job => job.status === 'failed')
      .map(job => job.job_id)
    setSelectedJobIds(new Set(failedJobIds))
  }

  // Clear selection
  const handleClearSelection = () => {
    setSelectedJobIds(new Set())
  }

  // Check if job is failed and can be retried
  const canRetry = (job: JobSearchResult) => job.status === 'failed'

  const handleStatusToggle = (status: string) => {
    const newFilters = new Set(statusFilters)
    if (newFilters.has(status)) {
      newFilters.delete(status)
    } else {
      newFilters.add(status)
    }
    setStatusFilters(newFilters)
    setCurrentPage(1)
  }

  const handleKindToggle = (kind: string) => {
    const newFilters = new Set(kindFilters)
    if (newFilters.has(kind)) {
      newFilters.delete(kind)
    } else {
      newFilters.add(kind)
    }
    // If all kinds deselected, reset to all selected
    if (newFilters.size === 0) {
      setKindFilters(new Set(['hourly', 'daily', 'reprocess', 'backfill', 'email']))
    } else {
      setKindFilters(newFilters)
    }
    setCurrentPage(1)
  }

  const handleClearFilters = () => {
    setSmartFilter(null)
    setStatusFilters(new Set(['pending', 'running', 'completed', 'failed']))  // Include all statuses
    setKindFilters(new Set(['hourly', 'daily', 'reprocess', 'backfill', 'email']))
    setHasErrorsFilter(false)
    setIsPendingFilter(false)
    setIsActuallyRunningFilter(false)
    setSearchTerm('')
    setOrgFilters(new Set())
    setHours(24)
    setCurrentPage(1)
    if (onFilterClear) {
      onFilterClear()
    }
  }

  const handleHoursChange = (value: string) => {
    setHours(parseInt(value))
    setCurrentPage(1)
  }

  // Status filter popup component for column header
  const StatusFilterPopup = () => {
    const statusOptions = ['pending', 'running', 'completed', 'failed']
    const hasFilter = statusFilters.size > 0 && statusFilters.size < 4
    const allSelected = statusFilters.size === 4
    const noneSelected = statusFilters.size === 0

    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={`h-6 px-2 ${hasFilter ? 'bg-primary/10 text-primary' : ''}`}
          >
            <Filter className="h-3 w-3" />
            {hasFilter && <span className="ml-1 text-xs">{statusFilters.size}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-3" align="start">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Filter by Status</span>
            {noneSelected ? (
              <Button variant="ghost" size="sm" onClick={() => { setStatusFilters(new Set(statusOptions)); setCurrentPage(1) }} className="h-6 px-2 text-xs">
                All
              </Button>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => { setStatusFilters(new Set()); setCurrentPage(1) }} className="h-6 px-2 text-xs">
                Clear
              </Button>
            )}
          </div>
          <div className="space-y-2">
            {statusOptions.map((status) => (
              <div key={status} className="flex items-center space-x-2">
                <Checkbox
                  id={`status-filter-${status}`}
                  checked={statusFilters.has(status)}
                  onCheckedChange={() => handleStatusToggle(status)}
                />
                <Label
                  htmlFor={`status-filter-${status}`}
                  className="text-sm cursor-pointer capitalize"
                >
                  {status}
                </Label>
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    )
  }

  // Organization filter popup component for column header
  // Toggle organization filter
  const handleOrgToggle = (orgId: number) => {
    setOrgFilters(prev => {
      const next = new Set(prev)
      if (next.has(orgId)) {
        next.delete(orgId)
      } else {
        next.add(orgId)
      }
      return next
    })
  }

  const OrgFilterPopup = () => {
    const organizations = filterOptions.organizations
    const hasFilter = orgFilters.size > 0
    const allSelected = orgFilters.size === organizations.length
    const noneSelected = orgFilters.size === 0

    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={`h-6 px-2 ${hasFilter ? 'bg-primary/10 text-primary' : ''}`}
          >
            <Filter className="h-3 w-3" />
            {hasFilter && <span className="ml-1 text-xs">{orgFilters.size}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-3" align="start">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Filter by Organization</span>
            {noneSelected ? (
              <Button variant="ghost" size="sm" onClick={() => { setOrgFilters(new Set(organizations.map(o => o.id))); setCurrentPage(1) }} className="h-6 px-2 text-xs">
                All
              </Button>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => { setOrgFilters(new Set()); setCurrentPage(1) }} className="h-6 px-2 text-xs">
                Clear
              </Button>
            )}
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {organizations.length === 0 ? (
              <span className="text-sm text-muted-foreground">No organizations found</span>
            ) : (
              organizations.map((org) => (
                <div key={org.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`org-filter-${org.id}`}
                    checked={orgFilters.has(org.id)}
                    onCheckedChange={() => handleOrgToggle(org.id)}
                  />
                  <Label
                    htmlFor={`org-filter-${org.id}`}
                    className="text-sm cursor-pointer truncate"
                    title={org.name}
                  >
                    {org.name}
                  </Label>
                </div>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>
    )
  }

  // Kind filter popup component for column header
  const KindFilterPopup = () => {
    const kindOptions = ['hourly', 'daily', 'reprocess', 'backfill', 'email']
    const hasFilter = kindFilters.size > 0 && kindFilters.size < 5
    const noneSelected = kindFilters.size === 0

    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={`h-6 px-2 ${hasFilter ? 'bg-primary/10 text-primary' : ''}`}
          >
            <Filter className="h-3 w-3" />
            {hasFilter && <span className="ml-1 text-xs">{kindFilters.size}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-3" align="start">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Filter by Collection</span>
            {noneSelected ? (
              <Button variant="ghost" size="sm" onClick={() => { setKindFilters(new Set(kindOptions)); setCurrentPage(1) }} className="h-6 px-2 text-xs">
                All
              </Button>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => { setKindFilters(new Set()); setCurrentPage(1) }} className="h-6 px-2 text-xs">
                Clear
              </Button>
            )}
          </div>
          <div className="space-y-2">
            {kindOptions.map((kind) => (
              <div key={kind} className="flex items-center space-x-2">
                <Checkbox
                  id={`kind-filter-${kind}`}
                  checked={kindFilters.has(kind)}
                  onCheckedChange={() => handleKindToggle(kind)}
                />
                <Label
                  htmlFor={`kind-filter-${kind}`}
                  className="text-sm cursor-pointer capitalize"
                >
                  {kind}
                </Label>
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    )
  }

  // Helper to get display kind (hourly/daily for periodic jobs based on behavior_category)
  const getDisplayKind = (job: JobSearchResult): string => {
    if (job.kind === 'periodic') {
      // For periodic jobs, use behavior_category to determine hourly vs daily
      const category = job.behavior_category || 'hourly'
      if (category === 'daily') return 'daily'
      return 'hourly' // hourly, learning, or any other category defaults to hourly
    }
    return job.kind || 'unknown'
  }

  // Filter jobs on frontend when multiple kinds selected (API only supports single kind)
  const displayJobs = kindFilters.size > 1 && kindFilters.size < 5
    ? jobs.filter(job => kindFilters.has(getDisplayKind(job)))
    : jobs

  // Format job duration
  // For finished jobs: shows actual duration (completed_at - started_at)
  // For running/queued jobs: shows elapsed time since started (now - started_at)
  const formatJobDuration = (job: JobSearchResult): string => {
    if (!job.started_at) return '-'

    // Helper to parse timestamp as UTC (same as Activities tab)
    const parseUtc = (ts: string): number => {
      if (!ts.endsWith('Z') && !ts.includes('+') && !ts.includes('-', 10)) {
        ts = ts + 'Z'
      }
      return new Date(ts).getTime()
    }

    const start = parseUtc(job.started_at)
    if (isNaN(start)) return '-'

    // Use completed_at if available, otherwise current time
    const end = job.completed_at ? parseUtc(job.completed_at) : Date.now()
    if (isNaN(end)) return '-'

    const diffMs = end - start
    if (diffMs < 0) return '-'

    // Show seconds for durations less than 1 minute
    const diffSeconds = Math.floor(diffMs / 1000)
    if (diffSeconds < 60) {
      return `${diffSeconds}s`
    }

    const diffMinutes = Math.floor(diffMs / (1000 * 60))
    const hours = Math.floor(diffMinutes / 60)
    const minutes = diffMinutes % 60

    if (hours === 0) {
      return `${minutes}m`
    }
    return `${hours}h${minutes}m`
  }

  // Format current time in UTC
  const formatNowUTC = (): string => {
    return new Date().toLocaleTimeString('en-US', { timeZone: 'UTC', hour: '2-digit', minute: '2-digit' }) + ' UTC'
  }

  // Check if any filters are active (not at default values)
  const hasActiveFilters = () => {
    // Smart filter active
    if (smartFilter) return true
    // Has errors filter active
    if (hasErrorsFilter) return true
    // Actually running filter active
    if (isActuallyRunningFilter) return true
    // Status filters not at default (queued, running, failed)
    const defaultStatuses = new Set(['pending', 'running', 'failed'])
    if (statusFilters.size !== defaultStatuses.size ||
        ![...statusFilters].every(s => defaultStatuses.has(s))) return true
    // Kind filters not at default (all 5 selected)
    if (kindFilters.size !== 5) return true
    // Search term active
    if (searchTerm) return true
    // Organization filter active
    if (orgFilters.size > 0) return true
    return false
  }

  // Get filter description
  const getFilterDescription = () => {
    const parts: string[] = []

    // Has errors filter
    if (hasErrorsFilter) {
      parts.push('with errors')
    }

    // Kind filter description
    const selectedKinds = Array.from(kindFilters)
    if (selectedKinds.length > 0 && selectedKinds.length < 4) {
      parts.push(selectedKinds.join(', '))
    }

    // Status filter description (only show if not using hasErrorsFilter since all statuses are shown)
    const selectedStatuses = Array.from(statusFilters)
    if (!hasErrorsFilter && selectedStatuses.length > 0 && selectedStatuses.length < 4) {
      parts.push(selectedStatuses.join(', '))
    }

    let description = parts.length > 0 ? parts.join(' + ') + ' jobs' : 'all jobs'

    // Add batch info if smart filter active
    if (smartFilter) {
      description += ` from batch ${smartFilter.batchLabel}`
    }

    return description
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Jobs Explorer</CardTitle>
            <CardDescription>
              Showing {getFilterDescription()} ({totalJobs} total) - Updated {formatNowUTC()}
            </CardDescription>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Clear Filters */}
            {hasActiveFilters() && (
              <Button variant="outline" size="sm" onClick={handleClearFilters}>
                <X className="h-4 w-4 mr-1" />
                Clear Filters
              </Button>
            )}
            {/* Period Selector */}
            <Select value={String(hours)} onValueChange={handleHoursChange}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Last Batch</SelectItem>
                <SelectItem value="24">Last 24 hours</SelectItem>
                <SelectItem value="48">Last 48 hours</SelectItem>
                <SelectItem value="72">Last 3 days</SelectItem>
                <SelectItem value="168">Last 7 days</SelectItem>
                <SelectItem value="336">Last 14 days</SelectItem>
                <SelectItem value="720">Last 30 days</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {/* Search Row */}
        <div className="flex flex-wrap gap-4 mb-4 items-center">
          {/* Integration Search */}
          <div className="flex-1 min-w-[200px] max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by integration ID or name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
              {searchTerm && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1 h-7 w-7 p-0"
                  onClick={() => setSearchTerm('')}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>


        {/* Error State */}
        {error && (
          <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && jobs.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            <span className="ml-3 text-muted-foreground">Loading jobs...</span>
          </div>
        )}

        {/* Empty State */}
        {!loading && displayJobs.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-lg font-medium text-muted-foreground">No jobs found</p>
            <p className="text-sm text-muted-foreground mt-1">
              {smartFilter ? 'No jobs match this filter' : 'Try adjusting your filters or search term'}
            </p>
          </div>
        )}

        {/* Bulk Actions Bar - shown when jobs are selected */}
        {selectedJobIds.size > 0 && (
          <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center justify-between">
            <span className="text-sm text-blue-800 dark:text-blue-200">
              {selectedJobIds.size} job{selectedJobIds.size !== 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearSelection}
              >
                Clear Selection
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleBulkRetry}
                disabled={retryingJobs.size > 0}
              >
                {retryingJobs.size > 0 ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RotateCcw className="h-4 w-4 mr-2" />
                )}
                Retry Failed Periods
              </Button>
            </div>
          </div>
        )}

        {/* Jobs Table */}
        {displayJobs.length > 0 && (
          <>
            {/* Select All Failed Jobs button */}
            {displayJobs.some(job => job.status === 'failed') && selectedJobIds.size === 0 && (
              <div className="mb-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSelectAllFailed}
                >
                  Select all failed jobs ({displayJobs.filter(j => j.status === 'failed').length})
                </Button>
              </div>
            )}

            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]"></TableHead>
                    <TableHead className="w-[80px]">Job ID</TableHead>
                    <TableHead className="w-[70px]">Batch</TableHead>
                    <TableHead className="w-[80px]">Int. ID</TableHead>
                    <TableHead>Integration</TableHead>
                    <TableHead className="w-[100px]">
                      <div className="flex items-center justify-between">
                        <span>Collection</span>
                        <KindFilterPopup />
                      </div>
                    </TableHead>
                    <TableHead className="w-[120px]">
                      <div className="flex items-center justify-between">
                        <span>Status</span>
                        <StatusFilterPopup />
                      </div>
                    </TableHead>
                    <TableHead className="w-[100px]">Step</TableHead>
                    <TableHead className="w-[280px]">Progress</TableHead>
                    <TableHead className="w-[100px]">Records</TableHead>
                    <TableHead className="w-[70px]">Duration</TableHead>
                    <TableHead className="w-[800px] min-w-[400px]">Error</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayJobs.map((job) => (
                    <TableRow key={job.job_id} className={selectedJobIds.has(job.job_id) ? 'bg-blue-50 dark:bg-blue-950' : ''}>
                      <TableCell>
                        {canRetry(job) && (
                          <Checkbox
                            checked={selectedJobIds.has(job.job_id)}
                            onCheckedChange={() => handleToggleSelection(job.job_id)}
                          />
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{job.job_id}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {job.created_at ? (() => {
                          const d = new Date(job.created_at)
                          const mm = String(d.getUTCMonth() + 1).padStart(2, '0')
                          const dd = String(d.getUTCDate()).padStart(2, '0')
                          const hh = String(d.getUTCHours()).padStart(2, '0')
                          return `${mm}${dd}${hh}`
                        })() : '-'}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        <Link
                          to={`/${orgSlug}/integrations/${job.integration_id}/edit`}
                          className="text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          {job.integration_id}
                        </Link>
                      </TableCell>
                      <TableCell className="font-medium">{job.integration_name}</TableCell>
                      <TableCell>
                        {(() => {
                          const displayKind = getDisplayKind(job)
                          return (
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              displayKind === 'hourly' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' :
                              displayKind === 'daily' ? 'bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300' :
                              displayKind === 'reprocess' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300' :
                              displayKind === 'email' ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900 dark:text-cyan-300' :
                              'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300'
                            }`}>
                              {displayKind}
                            </span>
                          )
                        })()}
                      </TableCell>
                      <TableCell>
                        <JobStatusBadge
                          status={job.status as any}
                          periodsRunning={job.periods_running}
                          periodsCompleted={job.periods_completed}
                        />
                      </TableCell>
                      <TableCell>
                        <span className="text-xs bg-muted px-2 py-1 rounded">
                          {job.current_step || 'pending'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <JobProgressBar
                          progressPct={job.progress_pct}
                          periodsCompleted={job.periods_completed}
                          totalPeriods={job.total_periods}
                          periodsFailed={job.periods_failed}
                          periodStart={job.period_start}
                          periodEnd={job.period_end}
                        />
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs">
                        {job.records_processed.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {formatJobDuration(job)}
                      </TableCell>
                      <TableCell className="text-xs w-[800px] min-w-[400px]">
                        {job.error_message ? (
                          <span className="text-red-600 dark:text-red-400 whitespace-pre-wrap break-all">
                            {job.error_message}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination Info */}
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Showing {displayJobs.length} of {totalJobs} jobs
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground px-2">
                  Page {currentPage}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => p + 1)}
                  disabled={displayJobs.length < itemsPerPage}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

export default JobsExplorer
